export interface FeeReminderEmailData {
  parentName: string;
  studentName: string;
  className: string;
  sectionName?: string;
  schoolName: string;
  academicYear?: string;
  feeDetails: FeeReminderItem[];
  totalDue: number;
  totalPaid: number;
  totalFee: number;
  paymentUrl: string;
  schoolLogoUrl?: string;
}

export interface FeeReminderItem {
  feeName: string;
  feeType: string;
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
  dueDate?: string;
  isOverdue: boolean;
  lateFee?: number;
  installments?: FeeReminderInstallment[];
}

export interface FeeReminderInstallment {
  installmentName: string;
  amount: number;
  paidAmount: number;
  pendingAmount: number;
  dueDate?: string;
  isOverdue: boolean;
  status: 'Paid' | 'Pending' | 'Overdue';
}

export function feeReminderEmail(data: FeeReminderEmailData): string {

  const progressPercent = data.totalFee > 0
    ? Math.min(100, Math.round((data.totalPaid / data.totalFee) * 100))
    : 0;

  const progressBarFill = progressPercent === 100
    ? '#2e7d32'
    : progressPercent > 50
      ? '#f57c00'
      : '#c62828';

  function currencyINR(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  function formatDate(dateStr?: string): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  function statusBadge(status: string, isOverdue: boolean): string {
    if (status === 'Paid') {
      return `<span style="background:#e8f5e9;color:#2e7d32;border:1px solid #a5d6a7;padding:2px 9px;border-radius:99px;font-size:11px;font-weight:700;text-transform:uppercase;">✓ Paid</span>`;
    }
    if (isOverdue) {
      return `<span style="background:#ffebee;color:#c62828;border:1px solid #ef9a9a;padding:2px 9px;border-radius:99px;font-size:11px;font-weight:700;text-transform:uppercase;">⚠ Overdue</span>`;
    }
    return `<span style="background:#fff3e0;color:#e65100;border:1px solid #ffcc80;padding:2px 9px;border-radius:99px;font-size:11px;font-weight:700;text-transform:uppercase;">⏳ Pending</span>`;
  }

  const feeRows = data.feeDetails.map(fee => {

    const installmentRows = fee.installments && fee.installments.length > 0
      ? `
        <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:10px;border-collapse:collapse;font-size:12px;">
          <thead>
            <tr style="background:#f5f5f5;">
              <th style="padding:7px 10px;text-align:left;color:#757575;font-weight:700;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #e0e0e0;">Installment</th>
              <th style="padding:7px 10px;text-align:center;color:#757575;font-weight:700;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #e0e0e0;">Due Date</th>
              <th style="padding:7px 10px;text-align:right;color:#757575;font-weight:700;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #e0e0e0;">Amount</th>
              <th style="padding:7px 10px;text-align:right;color:#757575;font-weight:700;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #e0e0e0;">Paid</th>
              <th style="padding:7px 10px;text-align:right;color:#757575;font-weight:700;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #e0e0e0;">Balance</th>
              <th style="padding:7px 10px;text-align:center;color:#757575;font-weight:700;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #e0e0e0;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${fee.installments.map((inst, i) => `
              <tr style="background:${i % 2 === 0 ? '#ffffff' : '#fafafa'};">
                <td style="padding:8px 10px;color:#212121;font-weight:600;border-bottom:1px solid #f0f0f0;">${inst.installmentName}</td>
                <td style="padding:8px 10px;text-align:center;color:${inst.isOverdue && inst.status !== 'Paid' ? '#c62828' : '#757575'};border-bottom:1px solid #f0f0f0;">${formatDate(inst.dueDate)}</td>
                <td style="padding:8px 10px;text-align:right;font-weight:600;color:#212121;border-bottom:1px solid #f0f0f0;">${currencyINR(inst.amount)}</td>
                <td style="padding:8px 10px;text-align:right;font-weight:600;color:#2e7d32;border-bottom:1px solid #f0f0f0;">${currencyINR(inst.paidAmount)}</td>
                <td style="padding:8px 10px;text-align:right;font-weight:700;color:${inst.pendingAmount > 0 ? '#c62828' : '#2e7d32'};border-bottom:1px solid #f0f0f0;">${currencyINR(inst.pendingAmount)}</td>
                <td style="padding:8px 10px;text-align:center;border-bottom:1px solid #f0f0f0;">${statusBadge(inst.status, inst.isOverdue)}</td>
              </tr>
            `).join('')}
            ${fee.lateFee && fee.lateFee > 0 ? `
              <tr style="background:#fff3e0;">
                <td colspan="4" style="padding:8px 10px;color:#e65100;font-weight:700;font-size:12px;">⚠ Late Fee Charges</td>
                <td style="padding:8px 10px;text-align:right;font-weight:700;color:#c62828;">${currencyINR(fee.lateFee)}</td>
                <td style="padding:8px 10px;text-align:center;">
                  <span style="background:#ffebee;color:#c62828;border:1px solid #ef9a9a;padding:2px 9px;border-radius:99px;font-size:11px;font-weight:700;text-transform:uppercase;">⚠ Overdue</span>
                </td>
              </tr>
            ` : ''}
          </tbody>
        </table>
      `
      : '';

    const feePct = fee.totalAmount > 0 ? Math.min(100, Math.round((fee.paidAmount / fee.totalAmount) * 100)) : 0;
    const feeBarColor = feePct === 100 ? '#2e7d32' : fee.isOverdue ? '#c62828' : '#f57c00';
    const borderColor = fee.isOverdue && feePct < 100 ? '#c62828' : feePct === 100 ? '#2e7d32' : '#e0e0e0';

    return `
      <div style="border:1px solid ${borderColor};border-left:4px solid ${borderColor};border-radius:8px;margin-bottom:14px;overflow:hidden;background:#ffffff;">

        <!-- Fee Header -->
        <div style="display:flex;align-items:center;justify-content:space-between;padding:14px 16px 10px;flex-wrap:wrap;gap:8px;">
          <div style="flex:1;">
            <div style="font-size:15px;font-weight:700;color:#212121;">${fee.feeName}</div>
            <div style="font-size:12px;color:#757575;margin-top:2px;">${fee.feeType}${fee.dueDate ? ` &nbsp;·&nbsp; Due: <span style="color:${fee.isOverdue && feePct < 100 ? '#c62828' : '#757575'};font-weight:${fee.isOverdue && feePct < 100 ? '700' : '400'};">${formatDate(fee.dueDate)}</span>` : ''}</div>
          </div>
          <div style="text-align:right;">
            <div style="font-size:13px;color:#757575;">Total: <strong style="color:#212121;">${currencyINR(fee.totalAmount)}</strong></div>
            <div style="margin-top:4px;">${statusBadge(feePct === 100 ? 'Paid' : fee.isOverdue ? 'Overdue' : 'Pending', fee.isOverdue)}</div>
          </div>
        </div>

        <!-- Paid / Pending row -->
        <div style="display:flex;padding:0 16px 10px;gap:16px;flex-wrap:wrap;">
          <div style="flex:1;min-width:100px;">
            <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#757575;margin-bottom:3px;">Paid</div>
            <div style="font-size:15px;font-weight:700;color:#2e7d32;">${currencyINR(fee.paidAmount)}</div>
          </div>
          <div style="width:1px;background:#e0e0e0;margin:2px 0;"></div>
          <div style="flex:1;min-width:100px;">
            <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#757575;margin-bottom:3px;">Pending</div>
            <div style="font-size:15px;font-weight:700;color:${fee.pendingAmount > 0 ? '#c62828' : '#2e7d32'};">${currencyINR(fee.pendingAmount)}</div>
          </div>
          ${fee.lateFee && fee.lateFee > 0 ? `
          <div style="width:1px;background:#e0e0e0;margin:2px 0;"></div>
          <div style="flex:1;min-width:100px;">
            <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#757575;margin-bottom:3px;">Late Fee</div>
            <div style="font-size:15px;font-weight:700;color:#e65100;">${currencyINR(fee.lateFee)}</div>
          </div>
          ` : ''}
        </div>

        <!-- Progress bar -->
        <div style="margin:0 16px 12px;height:4px;background:#eeeeee;border-radius:99px;overflow:hidden;">
          <div style="height:100%;width:${feePct}%;background:${feeBarColor};border-radius:99px;"></div>
        </div>

        <!-- Installment breakdown -->
        ${installmentRows ? `
          <div style="padding:0 16px 14px;">
            ${installmentRows}
          </div>
        ` : `
          <div style="padding:0 16px 12px;font-size:12px;color:#757575;">
            <i>One-time fee${feePct === 100 ? ' &nbsp;·&nbsp; <span style="color:#2e7d32;font-weight:600;">✓ Payment received</span>' : ''}</i>
          </div>
        `}

      </div>
    `;
  }).join('');

  const hasOverdue = data.feeDetails.some(f => f.isOverdue && f.pendingAmount > 0);

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Fee Payment Reminder — ${data.studentName}</title>
</head>

<body style="font-family:Arial,sans-serif;background:#eef1f7;padding:30px 16px;margin:0;">

  <div style="max-width:640px;margin:auto;background:#ffffff;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,0.10);overflow:hidden;">

    <!-- ── HEADER ─────────────────────────────────── -->
    <div style="background:#1a1a4e;padding:24px 28px;color:#ffffff;text-align:center;">
      ${data.schoolLogoUrl ? `<img src="${data.schoolLogoUrl}" alt="${data.schoolName}" style="height:48px;margin-bottom:12px;display:block;margin-left:auto;margin-right:auto;" />` : ''}
      <div style="font-size:13px;font-weight:600;letter-spacing:1px;text-transform:uppercase;color:rgba(255,255,255,.6);margin-bottom:6px;">${data.schoolName}</div>
      <h2 style="margin:0;font-size:22px;font-weight:700;color:#ffffff;">Fee Payment Reminder</h2>
      ${data.academicYear ? `<div style="margin-top:6px;font-size:12px;color:rgba(255,255,255,.55);">Academic Year: ${data.academicYear}</div>` : ''}
    </div>

    <!-- ── OVERDUE ALERT BANNER (conditional) ──────── -->
    ${hasOverdue ? `
    <div style="background:#ffebee;border-left:5px solid #c62828;padding:12px 28px;display:flex;align-items:center;gap:10px;">
      <span style="font-size:20px;">⚠️</span>
      <div>
        <div style="font-size:13px;font-weight:700;color:#c62828;">Overdue Fees Detected</div>
        <div style="font-size:12px;color:#b71c1c;margin-top:2px;">One or more fees are past their due date. Late charges may apply. Please pay immediately.</div>
      </div>
    </div>
    ` : ''}

    <!-- ── BODY ───────────────────────────────────── -->
    <div style="padding:28px 28px 20px;color:#212121;line-height:1.7;">

      <p style="font-size:15px;margin:0 0 6px;">Dear <strong>${data.parentName}</strong>,</p>

      <p style="font-size:14px;color:#555;margin:0 0 20px;">
        This is a friendly reminder regarding the pending fee payment for your ward.
        Please review the details below and complete the payment at the earliest.
      </p>

      <!-- Student Info Card -->
      <div style="background:#f5f6fa;border:1px solid #e0e0e0;border-radius:8px;padding:16px 18px;margin-bottom:22px;">
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.9px;color:#757575;margin-bottom:10px;">Student Details</div>
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="padding:4px 0;font-size:13px;color:#757575;width:40%;">Student Name</td>
            <td style="padding:4px 0;font-size:13px;font-weight:700;color:#1a1a4e;">${data.studentName}</td>
          </tr>
          <tr>
            <td style="padding:4px 0;font-size:13px;color:#757575;">Class</td>
            <td style="padding:4px 0;font-size:13px;font-weight:600;color:#212121;">${data.className}${data.sectionName ? ` &nbsp;·&nbsp; Section ${data.sectionName}` : ''}</td>
          </tr>
          <tr>
            <td style="padding:4px 0;font-size:13px;color:#757575;">School</td>
            <td style="padding:4px 0;font-size:13px;color:#212121;">${data.schoolName}</td>
          </tr>
          ${data.academicYear ? `
          <tr>
            <td style="padding:4px 0;font-size:13px;color:#757575;">Academic Year</td>
            <td style="padding:4px 0;font-size:13px;color:#212121;">${data.academicYear}</td>
          </tr>
          ` : ''}
        </table>
      </div>

      <!-- Summary Strip -->
      <div style="background:#1a1a4e;border-radius:8px;padding:18px 20px;margin-bottom:22px;">
        <div style="display:flex;flex-wrap:wrap;gap:12px;justify-content:space-between;">
          <div style="text-align:center;min-width:110px;">
            <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1px;color:rgba(255,255,255,.55);margin-bottom:4px;">Total Fee</div>
            <div style="font-size:18px;font-weight:700;color:#ffffff;">${currencyINR(data.totalFee)}</div>
          </div>
          <div style="text-align:center;min-width:110px;">
            <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1px;color:rgba(255,255,255,.55);margin-bottom:4px;">Paid</div>
            <div style="font-size:18px;font-weight:700;color:#a5d6a7;">${currencyINR(data.totalPaid)}</div>
          </div>
          <div style="text-align:center;min-width:110px;">
            <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1px;color:rgba(255,255,255,.55);margin-bottom:4px;">Outstanding</div>
            <div style="font-size:18px;font-weight:700;color:${data.totalDue > 0 ? '#ef9a9a' : '#a5d6a7'};">${currencyINR(data.totalDue)}</div>
          </div>
        </div>
        <!-- Progress bar -->
        <div style="margin-top:14px;">
          <div style="display:flex;justify-content:space-between;font-size:11px;color:rgba(255,255,255,.6);margin-bottom:6px;">
            <span>Payment Progress</span>
            <span style="font-weight:700;color:#fff;">${progressPercent}%</span>
          </div>
          <div style="height:6px;background:rgba(255,255,255,.2);border-radius:99px;overflow:hidden;">
            <div style="height:100%;width:${progressPercent}%;background:${progressBarFill};border-radius:99px;"></div>
          </div>
        </div>
      </div>

      <!-- Fee Breakdown Heading -->
      <div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#757575;margin-bottom:12px;padding-bottom:8px;border-bottom:2px solid #e0e0e0;">
        📋 Fee Breakdown
      </div>

      <!-- Fee Cards -->
      ${feeRows}

      <!-- CTA BUTTON -->
      <div style="text-align:center;margin:28px 0 20px;">
        <a href="${data.paymentUrl}"
           style="
             display:inline-block;
             background:#e53935;
             color:#ffffff;
             padding:14px 32px;
             text-decoration:none;
             border-radius:99px;
             font-weight:700;
             font-size:15px;
             letter-spacing:.3px;
           ">
          💳 &nbsp;Pay Now ${currencyINR(data.totalDue)}
        </a>
      </div>

      <p style="font-size:13px;color:#757575;border-top:1px solid #f0f0f0;padding-top:16px;">
        If you have already made the payment, please ignore this reminder or contact the school office
        with your payment receipt for reconciliation.
      </p>

      <p style="font-size:14px;margin-bottom:0;margin-top:20px;">
        Regards,<br>
        <strong style="color:#1a1a4e;">${data.schoolName}</strong>
      </p>

    </div>

    <!-- ── FOOTER ──────────────────────────────────── -->
    <div style="background:#f5f5f5;padding:16px 28px;text-align:center;border-top:1px solid #e0e0e0;">
      <p style="font-size:12px;color:#9e9e9e;margin:0 0 4px;">
        This is an automated fee reminder from <strong>${data.schoolName}</strong>.
      </p>
      <p style="font-size:11px;color:#bdbdbd;margin:0;">
        © ${new Date().getFullYear()} Nishwik School Platform &nbsp;·&nbsp;
        <a href="${data.paymentUrl}" style="color:#9e9e9e;text-decoration:none;">Payment Portal</a>
      </p>
    </div>

  </div>

</body>
</html>
  `;
}